import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MutableRefObject } from 'react';
import {
    ApiResponseStickerDesign,
    Board,
    StickerDesign,
    StickerDesignId,
} from '../utils/Primitives';
import { getGatewayUrlForCID } from './StickerDesigns';
import AddressDisplay from '../components/AddressDisplay';

interface LatestStickersProps {
    boardRef: MutableRefObject<Board>;
    fetchUrl: string;
    stickersPerPage: number;
    setPreviewStickerId: (stickerId: StickerDesignId) => void;
    currentPage: number;
    setCurrentPage: (page: number) => void;
    initialData?: any[];
}

export default function LatestStickers({
    boardRef,
    fetchUrl,
    stickersPerPage,
    setPreviewStickerId,
    currentPage,
    setCurrentPage,
    initialData
}: LatestStickersProps) {
    console.log('LatestStickers render - fetchUrl:', fetchUrl);
    console.log('LatestStickers render - initialData:', initialData);

    const { data: stickers, isLoading } = useQuery({
        queryKey: ['latestStickers', fetchUrl],
        queryFn: async () => {
            console.log('LatestStickers fetching data from:', fetchUrl);
            const response = await fetch(fetchUrl);
            if (!response.ok) {
                throw new Error('Failed to fetch stickers');
            }
            const data = await response.json();
            console.log('LatestStickers fetched data:', data);
            return data.data || [];
        },
        initialData,
        staleTime: 30000
    });

    const totalPages = Math.max(
        1,
        Math.ceil((stickers?.length || 0) / stickersPerPage)
    );
    const visibleStickers = stickers?.slice(
        (currentPage - 1) * stickersPerPage,
        currentPage * stickersPerPage
    ) || [];

    const handleNextPageClick = () => {
        setCurrentPage(Math.min(currentPage + 1, totalPages));
    };

    const handlePreviousPageClick = () => {
        setCurrentPage(Math.max(currentPage - 1, 1));
    };

    const handleDidClickSticker = (stickerId: string) => {
        console.log('Latest stickers handleDidClickSticker', stickerId);
        setPreviewStickerId(parseInt(stickerId));
    }

    return (
        <div className="mt-4 w-full pt-2 bg-white rounded-lg shadow p-3">
            <div className="relative flex items-center justify-center h-8 mb-2">
                {totalPages > 1 && (
                    <>
                        <div className="absolute left-0 top-1/2 -translate-y-1/2">
                            {currentPage > 1 ? (
                                <button
                                    onClick={handlePreviousPageClick}
                                    className={
                                        'text-lg px-3 py-0.5 rounded ' +
                                        'bg-slate-100 hover:bg-slate-200 ' +
                                        'select-none'
                                    }
                                >
                                    ‹
                                </button>
                            ) : (
                                <span className="invisible text-lg select-none">
                                    ‹
                                </span>
                            )}
                        </div>
                        <div className="absolute right-0 top-1/2 -translate-y-1/2">
                            {currentPage < totalPages ? (
                                <button
                                    onClick={handleNextPageClick}
                                    className={
                                        'text-lg px-3 py-0.5 rounded ' +
                                        'bg-slate-100 hover:bg-slate-200 ' +
                                        'select-none'
                                    }
                                >
                                    ›
                                </button>
                            ) : (
                                <span className="invisible text-lg select-none">
                                    ›
                                </span>
                            )}
                        </div>
                    </>
                )}
                <h3 className="text-xs font-medium select-none">Published Stickers</h3>
            </div>

            <div className="space-y-2">
                {isLoading ? (
                    <div className="flex justify-center py-4">
                        <div
                            className={
                                'animate-spin rounded-full h-6 w-6 ' +
                                'border-b-2 border-blue-500'
                            }
                        />
                    </div>
                ) : visibleStickers.length > 0 ? (
                    visibleStickers.map((sticker: ApiResponseStickerDesign) => {
                        const thumbWidth =
                            64 * Math.min(1, Number(sticker.aspectRatio));

                        const thumbHeight =
                            64 * Math.min(1, 1 / Number(sticker.aspectRatio));

                        return (
                            <div
                                key={sticker.stickerId}
                                className={
                                    'flex items-start gap-3 p-2 ' +
                                    'hover:cursor-pointer hover:shadow-lg ' +
                                    'hover:scale-105 transition-transform'
                                }
                                onClick={(e) => {
                                    handleDidClickSticker(sticker.stickerId);
                                    e.stopPropagation();
                                }}
                            >
                                <div
                                    className={
                                        'relative w-16 h-16 overflow-hidden ' +
                                        'rounded'
                                    }
                                    style={{
                                        width: `${thumbWidth}px`,
                                        height: `${thumbHeight}px`,
                                    }}
                                >
                                    <img
                                        src={
                                            getGatewayUrlForCID(
                                                sticker.imageCID
                                            ) || ''
                                        }
                                        alt={sticker.name}
                                        className="object-contain w-full h-full"
                                    />
                                </div>
                                <div className="flex flex-col justify-center">
                                    <div className="text-[10px] text-center">
                                        <AddressDisplay
                                            address={sticker.currentPublisher}
                                            ensName={
                                                sticker.currentPublisherEns ||
                                                undefined
                                            }
                                        />
                                    </div>
                                    <div className="py-1 text-xs text-center">
                                        {sticker.name}
                                    </div>
                                    <div className="py-1 text-xs text-center">
                                        Sticker ID: {sticker.stickerId}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="text-xs text-slate-500 flex justify-center">
                        None
                    </div>
                )}
            </div>
        </div>
    );
}
